import { AvailableRoom, CalendarAvailabiltyResponse } from '@features/rooms/types';
import * as Sentry from '@sentry/nextjs';
import request from '@utils/request';
import { some } from '@utils/some';
import { toCamelCase } from 'case-converter';
import { MultiRoomSearch } from 'shared-types';
import { getTotalFromPricesForRates } from '../rates/helpers/getTotalFromPricesForRates.helper';
import { getCommonItemsFromAvailability } from './helpers/getCommonItemsFromAvailability.helper';
import { getTotalPricesForRooms } from './helpers/getTotalPricesForRooms.helper';
import { sortExtrasByPriorityWithinCategory } from './sortExtras';
import {
  AvailableRoomByIdSchema,
  AvailableRoomsSchema,
  CalendarAvailabilityResponseSchema,
  RoomExtrasSchema,
  RoomAndRatePageSettingsSchema,
  StaticRoomByIdSchema,
  StaticRoomsSchema,
} from './schemas';

const ROOMS_REQUEST_TIMEOUT_MS = 10000;

export type CalendarAvailabiltyParams = {
  startDate: string;
  endDate: string;
  adults: number;
  children: number;
  roomCode?: string;
};

export type RoomParams = {
  fromDate: string;
  toDate: string;
  adults: number;
  children: number;
  childAges: { [child: string]: number };
  rooms: MultiRoomSearch;
  promoRateCode?: string;
};

export async function getCalendarAvailability({
  startDate,
  endDate,
  adults,
  children,
  roomCode,
  siteId,
}: CalendarAvailabiltyParams & { siteId: number }): Promise<CalendarAvailabiltyResponse> {
  const query = new URLSearchParams({ children: String(children) });

  if (roomCode) {
    query.set('room-code', roomCode);
  }

  const response = await request.base.get(
    `/${siteId}/rooms/calendar/${adults}/${startDate}/${endDate}/availability?${query.toString()}`
  );

  return CalendarAvailabilityResponseSchema.parse(response.data);
}

export async function getRoomPageSettings({
  siteId,
  language,
}: {
  siteId: number;
  language: string;
}) {
  const response = await request.base.get(`${siteId}/pages/rooms/${language}`);
  if (response.status === 204) {
    return null;
  }
  return RoomAndRatePageSettingsSchema.parse(response.data);
}

export async function getStaticRooms({ siteId, language }: { siteId: number; language?: string }) {
  const query = language ? `?${new URLSearchParams({ language }).toString()}` : '';
  const response = await request.base.get(`${siteId}/rooms/details${query}`);
  return StaticRoomsSchema.parse(response.data);
}

function checkRoomHasPricing(room: AvailableRoom) {
  return (
    room.price_per_night &&
    room.price_per_stay &&
    room.price_per_night?.amount !== 0 &&
    room.price_per_stay?.amount !== 0
  );
}

export async function getAvailableRooms({
  siteId,
  rooms,
  isMultiRoomsEnabled = false,
  ...params
}: RoomParams & { siteId: number; isMultiRoomsEnabled?: boolean }) {
  try {
    if (!isMultiRoomsEnabled) {
      const urlParams = buildQueryParams(params);
      const response = await request.base.get(`${siteId}/rooms?${urlParams}&per-page=0`, {
        timeout: ROOMS_REQUEST_TIMEOUT_MS,
      });

      Sentry.setTag('hasAvailableRoomsData', 1);

      const filteredResponseData = {
        ...response.data,
        data: response.data.data.filter((room) => checkRoomHasPricing(room)),
      };
      return AvailableRoomsSchema.parse(filteredResponseData);
    } else {
      //remove duplicate configs to minimise api requests
      const roomsWithoutDuplicateConfigs = removeRoomConfigDuplates(rooms);
      const meta = { page: 0, per_page: 0 };

      const allPossibleRooms = await Promise.all(
        roomsWithoutDuplicateConfigs.map(async ({ room, count }) => {
          const urlParams = buildQueryParams({
            ...params,
            adults: room.adults,
            children: room.children,
          });
          //for each room configuration make the request
          const response = await request.base.get(`${siteId}/rooms?${urlParams}&per-page=0`, {
            timeout: ROOMS_REQUEST_TIMEOUT_MS,
          });
          const filteredResponseData = response.data.data.filter((room) =>
            checkRoomHasPricing(room)
          );
          meta.page = Math.max(meta.page, response.data.page);
          meta.per_page = Math.max(meta.per_page, response.data.per_page);
          return filteredResponseData.map((r) => ({ ...r, count }));
        })
      );
      //map rooms pricing to be the total for all search rooms
      const roomPrices = getTotalPricesForRooms(allPossibleRooms);
      const commonRooms = getCommonItemsFromAvailability(allPossibleRooms, rooms, false);

      Sentry.setTag('hasAvailableRoomsData', 1);
      return AvailableRoomsSchema.parse({
        total: commonRooms.length,
        data: commonRooms.map((room) => ({ ...room, ...roomPrices.get(room.id) })),
        ...meta,
      });
    }
  } catch (error) {
    Sentry.setTag('hasAvailableRoomsData', 0);
    return { data: [] };
  }
}

export async function getStaticRoomById({
  siteId,
  id,
  language,
}: {
  siteId: number;
  id: string;
  language?: string;
}) {
  const query = language ? `?${new URLSearchParams({ language }).toString()}` : '';
  const response = await request.base.get(`/${siteId}/rooms/${id}/details${query}`);
  const room = StaticRoomByIdSchema.parse(response.data);

  if (room.is_published === false) {
    throw Object.assign(new Error(`Room ${id} is not published`), {
      response: { status: 404 },
    });
  }

  return room;
}

export const removeRoomConfigDuplates = (rooms: MultiRoomSearch) => {
  const countMap = {};

  const result = rooms.reduce((acc: { room: MultiRoomSearch[number]; count: number }[], room) => {
    const key = `${room.adults}-${room.children}`;

    if (key !== undefined) {
      if (!countMap[key]) {
        countMap[key] = 1;
        acc.push({
          room,
          count: 1,
        });
      } else {
        countMap[key]++;
        const existingObject = acc.find(({ room: r }) => `${r.adults}-${r.children}` === key);
        if (existingObject) {
          existingObject.count = countMap[key];
        }
      }
    }
    return acc;
  }, []);

  return result;
};

export async function getAvailableRoomById(
  params: RoomParams & { siteId: number; id: string; isMultiRoomsEnabled?: boolean }
) {
  const { siteId, id, rooms, isMultiRoomsEnabled = false, ...journeyParams } = params;
  if (!isMultiRoomsEnabled) {
    const urlParams = buildQueryParams(journeyParams);
    const response = await request.base.get(`/${siteId}/rooms/${id}?${urlParams}`);
    return AvailableRoomByIdSchema.parse(response.data);
  } else {
    const roomsWithoutDuplicateConfigs = removeRoomConfigDuplates(rooms);

    const allResponses = await Promise.all(
      roomsWithoutDuplicateConfigs.map(async ({ room, count }) => {
        const urlParams = buildQueryParams({
          ...journeyParams,
          adults: room.adults,
          children: room.children,
        });
        //for each room configuration make the request
        const response = await request.base.get(`/${siteId}/rooms/${id}?${urlParams}`);
        const parsedRoom = AvailableRoomByIdSchema.parse(response.data);
        return parsedRoom
          ? {
              ...parsedRoom,
              rates: parsedRoom.rates.map((r) => ({ ...r, count })),
            }
          : null;
      })
    );

    const definedResponses = allResponses.filter((r) => !!r);
    if (!some(definedResponses) || allResponses.length !== definedResponses.length) {
      return null;
    }

    const room = definedResponses[0];
    if (room) {
      //map rates "fromPrice" to be the total for all searched rooms
      const ratePrices = getTotalFromPricesForRates(definedResponses.map((r) => r?.rates || []));
      room.quantity = Math.min(...definedResponses.map((r) => r?.quantity || 0));
      room.rates = getCommonItemsFromAvailability(
        definedResponses.map((r) => r?.rates || []),
        rooms
      ).map((rate) => {
        return {
          ...rate,
          ...ratePrices.get(rate.id),
        };
      });
    }
    return room;
  }
}

export async function getRoomExtras(
  params: RoomParams & { roomId?: string; rateId?: string; siteId: number; language?: string }
) {
  const { roomId, rateId, siteId, adults, children, fromDate, toDate, language } = params;

  if (!rateId || !roomId) {
    return [];
  }

  const query = new URLSearchParams({ children: String(children) });
  if (language) {
    query.set('language', language);
  }
  const response = await request.base.get(
    `/${siteId}/rooms/${roomId}/rates/${rateId}/${adults}/${fromDate}/${toDate}/extras?${query.toString()}`
  );
  return sortExtrasByPriorityWithinCategory(
    RoomExtrasSchema.parse(toCamelCase(response.data?.data))
  );
}

export function buildQueryParams(params: Omit<RoomParams, 'rooms'>) {
  const childAgesValues = Object.values(params.childAges);
  const childAgesParam = childAgesValues.length
    ? { 'child-ages': childAgesValues.join(',') }
    : undefined;

  const promoParam = params.promoRateCode ? { 'promo-code': params.promoRateCode } : undefined;

  const searchParams = new URLSearchParams({
    'from-date': params.fromDate,
    'to-date': params.toDate,
    adults: params.adults.toString(),
    children: params.children.toString(),
    ...childAgesParam,
    ...promoParam,
  });

  const urlParams = searchParams.toString();

  return urlParams;
}
